import {isUndefined, last} from 'lodash';
import {createSelector} from 'reselect';

import {
  buildSharedInboxCounterKey,
  canBeImportingForCounterKey,
  findBoldCounter,
  inboxCounterKeyPrefix,
  selfInboxCounterKey,
  teamCounterKeyPrefix,
} from '../../../helpers/counterHelpers';
import {makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {InboxModel} from '../../../models/api/inboxModel';
import {CountersVisibilityModesEnum} from '../../../models/api/preferencesModel';
import {TeamModel} from '../../../models/api/teamModel';
import {WorkspaceFolderLabelName} from '../../../models/api/workspaceModel';
import {RInboxesFilter} from '../../../models/url/inboxesUrlModel';
import {WorkspaceDisplaySectionTrackingTypesEnum} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {getFlatChannels, getUserOwnedChannels} from '../../../store/data/channels/channelsSelectors';
import {getBoldCounters} from '../../../store/data/dataSelectors';
import {getUserTeamParticipatingInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {getChannelIdsFromImportJobs} from '../../../store/data/jobs/jobsSelectors';
import {
  getCounterDelegationId,
  getCounterKey,
  getCountersVisibility,
  makeGetCounterBadgeValue,
  makeGetCounterIsUnread,
} from '../../../store/data/preferences/countersSelectors';
import {getIsInboxVisible} from '../../../store/data/preferences/preferencesSelectors';
import {getShouldRecommendDeclutter} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {
  makeGetInboxesPath,
  makeGetIsSearching,
  makeGetIsSearchingLocally,
} from '../../../store/panels/inboxes/inboxesPanelSelectors';
import {InboxesPanelId} from '../../../store/panels/panelStateBase';
import {WorkspaceLoadingTypesEnum} from './line/workspaceLineMore';

/*
 * Props.
 */

export interface WorkspaceItemContainerProps {
  panelId: InboxesPanelId;
  to?: string;
  counterKey?: string;
  counterDelegationId?: number;
  countersVisibility?: CountersVisibilityModesEnum;
  inbox?: InboxModel;
  team?: TeamModel;
  label?: WorkspaceFolderLabelName;
  shouldAlphabetizeWorkspace: boolean;
  trackingSection?: WorkspaceDisplaySectionTrackingTypesEnum;
}

/*
 * Selectors.
 */

/** Extract the "to" property from the props. */
function getItemTo(_state: AppState, props: WorkspaceItemContainerProps) {
  return props.to;
}

/** Extract the id of the "inbox" property from the props. `WorkspaceInboxItemContainerBase` receives an `inbox` prop. */
function getInboxId(_state: AppState, props: WorkspaceItemContainerProps) {
  return props.inbox ? props.inbox.id : undefined;
}

/** Extract the specified team from the props. */
function getTeam(_state: AppState, props: WorkspaceItemContainerProps) {
  return props.team;
}

/** Extract the "teamId" property from the props. `WorkspaceTeamItemContainer` receives a `team` prop. */
function getTeamId(_state: AppState, props: WorkspaceItemContainerProps) {
  return props.team ? props.team.id : undefined;
}

/** Find the visible inboxes of a team. */
const getTeamInboxIds = createSelector(
  [getTeam, getUserTeamParticipatingInboxes, getIsInboxVisible],
  (team, inboxes, isInboxVisible) => {
    if (isUndefined(team)) {
      return [];
    }

    return inboxes.filter((i) => i.namespace === team.namespace && isInboxVisible(i.id)).map((i) => i.id);
  },
);

/** Check if the current item should be selected based on the location and "to" prop. */
export function makeGetIsSelected() {
  const getInboxesPath = makeGetInboxesPath();
  const getIsSearching = makeGetIsSearching();
  const getIsSearchingLocally = makeGetIsSearchingLocally();

  return createSelector(
    [getInboxesPath, getItemTo, getIsSearching, getIsSearchingLocally],
    (inboxesPath, itemTo, isSearching, isSearchingLocally) => {
      if (!itemTo) {
        return false;
      }

      // We don't want to select anything when not searching locally.
      if (isSearching && !isSearchingLocally) {
        return false;
      }

      if (inboxesPath === itemTo) {
        return true;
      }

      const itemToParts = itemTo.split('/');
      const maybeFilterPart = last(itemToParts);
      const itemToWithoutFilter = itemToParts.slice(0, -1).join('/');
      const selectedItemTo = RInboxesFilter.is(maybeFilterPart) ? itemToWithoutFilter : itemTo;

      return inboxesPath.startsWith(`${selectedItemTo}/`);
    },
  );
}

/** Find whether this is an unread badge from the specified visibility. */
export function makeGetIsUnreadBadge() {
  return createSelector(
    getCountersVisibility,
    (visibility) => visibility === CountersVisibilityModesEnum.UNREAD,
  );
}

/** Find whether the team is unread by checking the nested inboxes. */
export function makeTeamGetIsUnread() {
  return createSelector(
    [getTeamInboxIds, getBoldCounters, getCountersVisibility, getCounterDelegationId],
    (inboxIds, boldCounters, visibility, delegationId) => {
      if (delegationId) {
        return false;
      }

      return inboxIds.some((id) => {
        const counterKey = buildSharedInboxCounterKey(id, visibility);
        return findBoldCounter(boldCounters, visibility, counterKey);
      });
    },
  );
}

/** Determine whether there is an ongoing import that would affect the counter. */
function makeGetIsImporting() {
  return createSelector(
    [
      getCounterKey,
      getCounterDelegationId,
      getInboxId,
      getTeamId,
      getUserOwnedChannels,
      getFlatChannels,
      getUserTeamParticipatingInboxes,
      getChannelIdsFromImportJobs,
    ],
    (
      key,
      delegationId,
      inboxId,
      teamId,
      userOwnedChannels,
      channels,
      teamInboxes,
      channelIdsFromImportJobs,
    ) => {
      // If we don't have a key, or this is a delegated inbox, or the key is not one that may be affected by an import, return false.
      if (!key || delegationId || !canBeImportingForCounterKey(key)) {
        return false;
      }

      let channelsForKey;

      if (key === selfInboxCounterKey) {
        // If the key refers to the user's own inbox, the relevant channels are user owned.
        channelsForKey = userOwnedChannels;
      } else if (key.startsWith(inboxCounterKeyPrefix) && inboxId) {
        // If the key refers to an inbox, the relevant channels are those that are part of the inbox.
        channelsForKey = channels.filter((c) => c.inboxId === inboxId);
      } else if (key.startsWith(teamCounterKeyPrefix) && teamId) {
        // If the key refers to a team, the relevant channels are those belonging to the inboxes for the team.
        const inboxIdsForKey = teamInboxes.filter((i) => i.namespace === `tim:${teamId}`).map((i) => i.id);
        channelsForKey = channels.filter((c) => inboxIdsForKey.includes(c.inboxId));
      }

      // If there are no channels that might be affected, nothing to do.
      if (!channelsForKey) {
        return false;
      }

      const channelIdsForKey = channelsForKey.map((c) => c.id);

      // See if there is a running job for a channel corresponding to the counter key.
      return Boolean(channelIdsFromImportJobs.find((id) => channelIdsForKey.includes(id)));
    },
  );
}

function makeGetLoadingType() {
  return createSelector(makeGetIsImporting(), (isImporting) => {
    if (isImporting) {
      return WorkspaceLoadingTypesEnum.IMPORT;
    }

    return undefined;
  });
}

/*
 * Redux.
 */

export function createCommonWorkspaceItemSelectors() {
  return {
    isSelected: makeGetIsSelected(),
    isUnreadBadge: makeGetIsUnreadBadge(),
    badgeValue: makeGetCounterBadgeValue(),
    loadingType: makeGetLoadingType(),
    hasRarelyViewedItems: getShouldRecommendDeclutter,
  };
}

export const workspaceItemMapStateToProps = makeMapStateToProps<WorkspaceItemContainerProps>()(() => {
  const commonSelectors = createCommonWorkspaceItemSelectors();
  return {
    ...commonSelectors,
    isUnread: makeGetCounterIsUnread(commonSelectors.badgeValue),
  };
});
